


import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import dayjs from "dayjs";
import { DateRangePickerComponent, DatePickerComponent } from "@syncfusion/ej2-react-calendars";
import { Typography, Switch, FormControl, Select, MenuItem, Button, Tooltip } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { historyStartDate } from "../../utils/historyWindow";
import "./ChartControls.css";

const MODES = ["minute", "hour", "day", "week", "month", "epg"];

/* =========================
   TIME DATA
========================= */

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));

/* =========================
   HELPERS
========================= */

const toDate = (v) => (v ? dayjs(v, "YYYY-MM-DD").toDate() : null);
const toKey = (d) => (d ? dayjs(d).format("YYYY-MM-DD") : "");

/* =========================
   TIME PICKER BOX
========================= */

function TimePickerBox({ label, value, onChange }) {
  const [open, setOpen] = useState(false);

  const anchorRef = useRef(null); // wrapper
  const popupRef = useRef(null);  // portal popup container
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const [h = "00", m = "00"] = (value || "00:00").split(":");

  useEffect(() => {
    function handleOutside(e) {
      const anchorEl = anchorRef.current;
      const popupEl = popupRef.current;

      const clickedInAnchor = anchorEl && anchorEl.contains(e.target);
      const clickedInPopup = popupEl && popupEl.contains(e.target);

      // If click is inside anchor OR inside popup, do nothing.
      if (clickedInAnchor || clickedInPopup) return;

      setOpen(false);
    }

    // Use mousedown is fine now because we whitelist popupRef too.
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  function openPopup() {
    const anchorEl = anchorRef.current;
    if (!anchorEl) return;

    const r = anchorEl.getBoundingClientRect();
    setPos({
      top: r.bottom + 6 + window.scrollY,
      left: r.right - 140 + window.scrollX,
    });
    setOpen(true);
  }

  function setHour(hh) {
    onChange?.(`${hh}:${m}`);
  }

  function setMinute(mm) {
    onChange?.(`${h}:${mm}`);
    setOpen(false);
  }

  return (
    <div className="toolbar-group time-wrapper" ref={anchorRef}>
      <span className="toolbar-label">{label}</span>

      <div
        className={`time-box ${open ? "active" : ""}`}
        onClick={() => (open ? setOpen(false) : openPopup())}
      >
        <span className="time-icon">🕒</span>
        <span className="time-value">
          {h}:{m}
        </span>
      </div>

      {open &&
        createPortal(
          <div
            ref={popupRef}
            className="time-popup"
            style={{
              position: "absolute",
              top: pos.top,
              left: pos.left,
              zIndex: 10000,
            }}
          >
            <div className="time-column">
              {HOURS.map((hh) => (
                <div
                  key={hh}
                  className={`time-item ${hh === h ? "selected" : ""}`}
                  onClick={() => setHour(hh)}
                >
                  {hh}
                </div>
              ))}
            </div>

            <div className="time-column">
              {MINUTES.map((mm) => (
                <div
                  key={mm}
                  className={`time-item ${mm === m ? "selected" : ""}`}
                  onClick={() => setMinute(mm)}
                >
                  {mm}
                </div>
              ))}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

/* =========================
   MAIN COMPONENT
========================= */

export default function ChartControls({
  mode,
  onModeChange,
  allowedModes,

  channelId,

  startDateInput,
  endDateInput,
  onStartDateChange,
  onEndDateChange,

  startTime,
  endTime,
  onStartTimeChange,
  onEndTimeChange,

  hideModeButtons = false,

  // Refresh controls (optional)
  refreshEnabled,
  onRefreshToggle,
  refreshIntervalMin,
  onRefreshIntervalChange,
  countdown,
  onManualRefresh,
  enableFourthDate,
  fourthDate,
  onFourthDateChange,

  // Extra controls (e.g. the TTV linear/log scale toggle), rendered in the
  // same toolbar row as the mode buttons and refresh controls.
  extraControls,
}) {
  const today = new Date();
  const earliest = historyStartDate(channelId);
  const modesToRender = allowedModes ?? MODES;

  const showTime = true;
  const singleDayOnly = mode === "minute" || mode === "epg" || mode === "ttv";

  // Handler for PT (Primetime) button
  function setPrimetime() {
    onStartTimeChange?.("19:00");
    onEndTimeChange?.("23:59");
  }

  function formatSeconds(sec = 0) {
    const m = String(Math.floor(sec / 60)).padStart(2, "0");
    const s = String(sec % 60).padStart(2, "0");
    return `${m}:${s}`;
  }

  return (
    <div className="chart-controls">
      <div className="chart-toolbar">
        {/* LEFT: Date Picker + Time Pickers */}
        <div className="toolbar-left">
          <div className="toolbar-group">
            <span className="toolbar-label">Date</span>

            {singleDayOnly ? (
              <DatePickerComponent
                value={toDate(startDateInput)}
                min={earliest}
                max={today}
                showClearButton={false}
                cssClass="dashboard-range-picker"
                format="dd/MM/yyyy"
                change={(e) => {
                  if (e?.value) {
                    const d = toKey(e.value);
                    onStartDateChange?.(d);
                  }
                }}
              />
            ) : (
              <DateRangePickerComponent
                startDate={toDate(startDateInput)}
                endDate={toDate(endDateInput)}
                min={earliest}
                max={today}
                showClearButton={false}
                cssClass="dashboard-range-picker"
                format="dd/MM/yyyy"
                change={(e) => {
                  if (e?.startDate) onStartDateChange?.(toKey(e.startDate));
                  if (e?.endDate) onEndDateChange?.(toKey(e.endDate));
                }}
              />
            )}
          </div>

        {showTime && (
          <>
            <TimePickerBox
              label="From"
              value={startTime || "00:00"}
              onChange={onStartTimeChange}
            />
            <TimePickerBox
              label="To"
              value={endTime || "23:59"}
              onChange={onEndTimeChange}
            />

    {(mode === "minute" || mode === "epg") && (
      <>
        {enableFourthDate && mode === "minute" && (
          <div className="toolbar-group fourth-date-group">
            <span className="toolbar-label">Fourth</span>
            <div className="fourth-date-wrapper">
              <DatePickerComponent
                value={fourthDate ? toDate(fourthDate) : null}
                min={earliest}
                max={today}
                showClearButton={true}
                cssClass="dashboard-range-picker"
                format="dd/MM/yyyy"
                placeholder="dd/mm/yyyy"
                change={(e) => {
                  if (e?.value) {
                    onFourthDateChange?.(toKey(e.value));
                  } else {
                    onFourthDateChange?.(null);
                  }
                }}
              />
            </div>
          </div>
        )}

    <Tooltip title="Set Primetime (19:00 - 23:59)" arrow>
      <Button
        size="small"
        variant="contained"
        color="secondary"
        startIcon={<AccessTimeIcon sx={{ fontSize: 18 }} />}
        sx={{ ml: 2, height: 36, fontWeight: 700, borderRadius: 2, boxShadow: 1, textTransform: "none", px: 2 }}
        onClick={setPrimetime}
      >
        PT
      </Button>
    </Tooltip>
  </>
)}
          </>
        )}
        </div>

        {/* RIGHT: Mode Buttons and Refresh Controls */}
        <div className="toolbar-right">
          {!hideModeButtons && (
            <div className="chart-mode-buttons">
              {modesToRender.map((m) => (
                <button
                  key={m}
                  className={mode === m ? "active" : ""}
                  onClick={() => onModeChange?.(m)}
                >
                  {m === "ttv" ? "TMTV" : m.toUpperCase()}
                </button>
              ))}
            </div>
          )}

          {extraControls}

          {/* Refresh Timer Controls */}
          {typeof refreshEnabled !== "undefined" && (
            <>
              <Typography fontWeight={700} sx={{ ml: 2, mr: 1, fontSize: 13 }}>Refresh Timer</Typography>
              <Switch checked={refreshEnabled} onChange={e => onRefreshToggle?.(e.target.checked)} />
              {refreshEnabled && (
                <FormControl size="small" sx={{ minWidth: 90, mx: 1 }}>
                  <Select
                    value={refreshIntervalMin}
                    onChange={e => onRefreshIntervalChange?.(Number(e.target.value) || 1)}
                  >
                    <MenuItem value={1}>1 min</MenuItem>
                    <MenuItem value={5}>5 min</MenuItem>
                    <MenuItem value={10}>10 min</MenuItem>
                    <MenuItem value={20}>20 min</MenuItem>
                  </Select>
                </FormControl>
              )}
              <Button size="small" variant="contained" sx={{ ml: 1 }} onClick={onManualRefresh}>
                {refreshEnabled ? formatSeconds(countdown) : "Manual Refresh"}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
